import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PointOfSale as CashierIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import ListNavigation from '../components/ListNavigation';
import { Link as RouterLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import CheckDetailView from '../components/checks/CheckDetailView';
import AddPaymentDialog from '../components/checks/AddPaymentDialog';
import { hasPermission } from '../utils/permissions';
import ItemAuditLogDialog, { getAuditStatusLabelCashiering } from '../components/checks/ItemAuditLogDialog';
import { formatDateTime } from '../utils/dateFormat';
import { CHECK_STATUSES, DIAMOND_TYPES, ORDER_STATUSES } from '../constants/appConstants';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../constants/roles';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getCheckStatusColor, getCheckStatusLabel, canStaffDeleteEmptyCheck, isEmptyCheckDeletable } from '../utils/checkDisplay';
import { canShowStaffCheckoutToCheck } from '../utils/cartStaffCheckout';
import {
  getClientDisplayName,
  getItemRef,
  getOrderId,
  getItemStatusColorClientDetail,
  getItemStatusLabel,
  isCustomByWeightItem,
} from '../utils/orderItemDisplay';
import BulkCancelOrderItemsDialog from '../components/orderItems/BulkCancelOrderItemsDialog';
import {
  cancelOrderItems,
  defaultReturnToInventoryForCancel,
} from '../utils/orderItemCancel';
import { useCheckPaymentDialog } from '../hooks/useCheckPaymentDialog';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { getApiErrorInfo } from '../utils/apiErrors';
import { useFeedback, usePageNotice } from '../context/FeedbackContext';
import ExchangeRateMissingAlert from '../components/checks/ExchangeRateMissingAlert';
import { getBackTarget, mergeNavState, getClientTabPath } from '../utils/navigationReturn';
import CheckoutLeftoversDialog from '../components/cart/CheckoutLeftoversDialog';
import { useCheckoutLeftoversNav } from '../hooks/useCheckoutLeftoversNav';
import { buttonStartIcon } from '../utils/buttonLoading';

function CheckDetailPageShell({ embedded, children }) {
  if (embedded) {
    return <Box sx={{ pt: 0.5 }}>{children}</Box>;
  }
  return (
    <Layout>
      <Container maxWidth="xl" disableGutters>
        {children}
      </Container>
    </Layout>
  );
}

function CashieringCheckDetail({ embedded = false, checkId: checkIdProp, clientId: clientIdProp }) {
  const { checkId: routeCheckId } = useParams();
  const checkId = checkIdProp || routeCheckId;
  const navigate = useNavigate();
  const location = useLocation();
  const { leftoversOpen, leftoversRows, closeLeftovers } = useCheckoutLeftoversNav();
  const listNav = location.state || {};
  const navIds = listNav.ids || [];
  const navIndex = navIds.length > 0 ? navIds.indexOf(checkId) : (listNav.index ?? -1);
  const hasPrev = navIds.length > 0 && navIndex > 0;
  const hasNext = navIds.length > 0 && navIndex < navIds.length - 1;
  const backTarget = embedded && clientIdProp
    ? { path: getClientTabPath(clientIdProp, 'checks'), labelKey: 'clients.backToChecks' }
    : getBackTarget(listNav, { path: '/cashiering', labelKey: 'cashiering.backToCashiering' });

  const goBack = () => {
    navigate(backTarget.path, backTarget.state ? { state: backTarget.state } : undefined);
  };

  const goToPrev = () => {
    if (!hasPrev) return;
    const prevId = navIds[navIndex - 1];
    const path = embedded && clientIdProp
      ? getClientTabPath(clientIdProp, 'checks', prevId)
      : `/cashiering/${prevId}`;
    navigate(path, {
      state: mergeNavState({ ids: navIds, index: navIndex - 1 }, listNav),
    });
  };

  const goToNext = () => {
    if (!hasNext) return;
    const nextId = navIds[navIndex + 1];
    const path = embedded && clientIdProp
      ? getClientTabPath(clientIdProp, 'checks', nextId)
      : `/cashiering/${nextId}`;
    navigate(path, {
      state: mergeNavState({ ids: navIds, index: navIndex + 1 }, listNav),
    });
  };

  const { t } = useTranslation();
  const { confirm, showError, showSuccess } = useFeedback();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userRole, isClient, isInternal, customPermissions } = useAuth();
  const isManager = userRole === ROLES.MANAGER;
  const canManageChecks = isInternal && hasPermission(userRole, 'manage_checks', customPermissions);
  const canRemoveCheckItem = isInternal && hasPermission(userRole, 'remove_check_item', customPermissions);
  const canViewCheck = canManageChecks || canRemoveCheckItem;
  const canManageOrders = isInternal && hasPermission(userRole, 'manage_orders', customPermissions);
  const canTakePayments = canManageOrders && canManageChecks;
  const canChangeStatus = isInternal && hasPermission(userRole, 'change_status', customPermissions);
  const canCancelCheckItems = canChangeStatus;
  const canEditOrderItemPrice = isInternal && hasPermission(userRole, 'edit_order_item_price', customPermissions);
  const canEditOrderItemWeight = isInternal && hasPermission(userRole, 'edit_order_item_weight', customPermissions);

  const canApplyCheckDiscount = isInternal && hasPermission(userRole, 'apply_check_discount', customPermissions);

  const [checkDetail, setCheckDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState(null);

  usePageNotice(
    `cashiering-check-error-${checkId}`,
    detailError && detailError.code !== 'MISSING_EXCHANGE_RATE'
      ? { message: detailError.message, severity: 'error', persist: true }
      : null,
  );
  const {
    paymentDialogOpen,
    paymentDialogPreset,
    openPaymentDialog,
    closePaymentDialog,
    openPayAndCloseDialog,
  } = useCheckPaymentDialog(t);
  const [itemEdits, setItemEdits] = useState({});
  const [savingItemId, setSavingItemId] = useState(null);
  const [itemAuditOpen, setItemAuditOpen] = useState(false);
  const [itemAuditLogs, setItemAuditLogs] = useState([]);
  const [itemAuditLoading, setItemAuditLoading] = useState(false);
  const [itemAuditRef, setItemAuditRef] = useState('');
  const [checkCancelDialogOpen, setCheckCancelDialogOpen] = useState(false);
  const [checkCancelItems, setCheckCancelItems] = useState([]);
  const [checkCancelLoading, setCheckCancelLoading] = useState(false);
  const { run: runAction, isPending } = useAsyncAction();

  const checksBasePath = isClient ? '/api/checks/my' : '/api/checks';

  const fetchCheckDetail = useCallback(async () => {
    if (!checkId) return;
    if (isInternal && !canViewCheck) {
      setDetailLoading(false);
      setCheckDetail(null);
      setDetailError({ message: t('users.noPermission') });
      return;
    }
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await axios.get(`${checksBasePath}/${checkId}`);
      setCheckDetail(res.data);
    } catch (error) {
      console.error('Failed to fetch check detail:', error);
      setDetailError(getApiErrorInfo(error));
      setCheckDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [checkId, checksBasePath, isInternal, canViewCheck, t]);

  useEffect(() => {
    fetchCheckDetail();
  }, [fetchCheckDetail]);

  const handleCheckDiscountUpdated = async (data) => {
    if (data?.check && data?.balance) {
      setCheckDetail((prev) => (prev ? {
        ...prev,
        check: data.check,
        balance: { ...prev.balance, ...data.balance },
      } : prev));
    } else {
      await fetchCheckDetail();
    }
  };

  const setItemEdit = (itemId, field, value) => {
    setItemEdits((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [field]: value } }));
  };

  const handleSaveItemEdit = async (item) => {
    const edits = itemEdits[item._id];
    if (!edits || (edits.pricePerUnit === undefined && edits.weight === undefined)) return;
    const groupId = getOrderId(item);
    setSavingItemId(item._id);
    try {
      const payload = {};
      if (edits.pricePerUnit !== undefined) payload.pricePerUnit = Number(edits.pricePerUnit);
      if ((item.type === DIAMOND_TYPES.NON_CERTIFIED || isCustomByWeightItem(item)) && edits.weight !== undefined) {
        payload.weight = Number(edits.weight);
      }
      if (groupId) {
        await axios.patch(`/api/orders/${groupId}/items/${item._id}`, payload);
      } else {
        await axios.patch(`/api/order-items/${item._id}/pricing`, payload);
      }
      await fetchCheckDetail();
      setItemEdits((prev) => {
        const next = { ...prev };
        delete next[item._id];
        return next;
      });
    } catch (error) {
      showError(error.response?.data?.error || t('cashiering.failedToSave'));
    } finally {
      setSavingItemId(null);
    }
  };

  const handleItemToDelivered = async (item) => {
    const groupId = getOrderId(item);
    await runAction(async () => {
      try {
        if (groupId) {
          await axios.patch(`/api/orders/${groupId}/items/${item._id}/status`, {
            status: ORDER_STATUSES.MEMO,
          });
        } else {
          await axios.patch(`/api/order-items/${item._id}/status`, {
            status: ORDER_STATUSES.MEMO,
          });
        }
        await fetchCheckDetail();
      } catch (error) {
        showError(error.response?.data?.error || t('cashiering.failedToMarkDelivered'));
      }
    }, `deliver-${item._id}`);
  };

  const openItemAuditLog = async (item) => {
    setItemAuditRef(getItemRef(item));
    setItemAuditOpen(true);
    setItemAuditLogs([]);
    setItemAuditLoading(true);
    try {
      const res = await axios.get('/api/audit-logs', { params: { orderItem: item._id, limit: 100 } });
      setItemAuditLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch item audit logs:', error);
    } finally {
      setItemAuditLoading(false);
    }
  };

  const closeItemAuditLog = () => {
    setItemAuditOpen(false);
    setItemAuditRef('');
  };

  const handlePayAndClose = () => {
    openPayAndCloseDialog(checkDetail?.balance);
  };

  const handleCancelPayment = async (paymentId) => {
    if (!(await confirm(t('cashiering.confirmCancelPayment')))) return;
    if (!checkDetail?.check) return;
    await runAction(async () => {
      try {
        await axios.delete(`${checksBasePath}/${checkDetail.check._id}/payments/${paymentId}`);
        await fetchCheckDetail();
      } catch (error) {
        showError(error.response?.data?.error || t('cashiering.failedToCancelPayment'));
      }
    }, `cancel-payment-${paymentId}`);
  };

  const handleRemoveItem = async (itemId) => {
    if (!canRemoveCheckItem) return;
    if (!(await confirm(t('cashiering.confirmRemoveItem')))) return;
    if (!checkDetail?.check) return;
    await runAction(async () => {
      try {
        await axios.delete(`${checksBasePath}/${checkDetail.check._id}/items/${itemId}`);
        await fetchCheckDetail();
      } catch (error) {
        showError(error.response?.data?.error || t('cashiering.failedToRemoveItem'));
      }
    }, `remove-${itemId}`);
  };

  const handleBulkRemoveItems = async (itemIds) => {
    if (!canRemoveCheckItem || !itemIds?.length || !checkDetail?.check) return;
    const confirmed = await confirm({
      title: t('checks.removeSelectedFromCheck'),
      message: t('checks.removeSelectedFromCheckConfirm', { count: itemIds.length }),
      confirmLabel: t('cashiering.removeFromCheck'),
      confirmColor: 'warning',
    });
    if (!confirmed) return;
    await runAction(async () => {
      try {
        await axios.post(`${checksBasePath}/${checkDetail.check._id}/items/remove`, { itemIds });
        await fetchCheckDetail();
        showSuccess(t('checks.removeSelectedSuccess', { count: itemIds.length }));
      } catch (error) {
        showError(error.response?.data?.error || t('cashiering.failedToRemoveItem'));
        await fetchCheckDetail();
      }
    }, 'bulk-remove-check-items');
  };

  const openBulkCancelCheckItems = (itemsToCancel) => {
    if (!canCancelCheckItems || !itemsToCancel?.length) return;
    setCheckCancelItems(itemsToCancel.map((item) => ({
      ...item,
      returnToInventory: defaultReturnToInventoryForCancel(item),
    })));
    setCheckCancelDialogOpen(true);
  };

  const closeBulkCancelCheckItems = () => {
    setCheckCancelDialogOpen(false);
    setCheckCancelItems([]);
  };

  const confirmBulkCancelCheckItems = async () => {
    if (!canCancelCheckItems || !checkCancelItems.length) return;
    setCheckCancelLoading(true);
    try {
      await runAction(async () => {
        try {
          await cancelOrderItems(checkCancelItems);
          await fetchCheckDetail();
          showSuccess(t('checks.cancelSelectedSuccess', { count: checkCancelItems.length }));
          closeBulkCancelCheckItems();
        } catch (error) {
          showError(error.response?.data?.error || t('orders.failedToCancelItem'));
          throw error;
        }
      }, 'bulk-cancel-check-items');
    } finally {
      setCheckCancelLoading(false);
    }
  };

  const canDeleteEmptyCheck = canStaffDeleteEmptyCheck({
    isInternal,
    userRole,
    customPermissions,
    hasPermissionFn: hasPermission,
    canCheckoutToCheckFn: canShowStaffCheckoutToCheck,
  });

  const showDeleteEmptyCheck = checkDetail && canDeleteEmptyCheck && isEmptyCheckDeletable({
    items: checkDetail.items,
    transactions: checkDetail.transactions,
    checkStatus: checkDetail.check.status,
  });

  const handleDeleteEmptyCheck = async () => {
    if (!checkDetail?.check) return;
    const confirmed = await confirm({
      title: t('checks.deleteEmpty'),
      message: t('checks.deleteEmptyConfirm'),
      confirmLabel: t('common.delete'),
      confirmColor: 'error',
    });
    if (!confirmed) return;
    await runAction(async () => {
      try {
        await axios.delete(`${checksBasePath}/${checkDetail.check._id}`);
        showSuccess(t('checks.deleteEmptySuccess'));
        goBack();
      } catch (error) {
        showError(error.response?.data?.error || t('checks.deleteEmptyFailed'));
      }
    }, 'delete-empty-check');
  };

  const clientUser = checkDetail?.check?.clientUser;
  const clientId = clientUser?._id ?? clientUser;

  const checkTitle = checkDetail?.check
    ? (checkDetail.check.name || t('cashiering.checkNumber', { id: checkDetail.check._id.slice(-6) }))
    : '';

  const renderCheckClientLine = () => (
    !isClient && clientUser ? (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        {t('cashiering.client')}:{' '}
        {clientId ? (
          <Link
            component={RouterLink}
            to={getClientTabPath(clientId, 'items')}
            underline="hover"
            sx={{ fontWeight: 600, wordBreak: 'break-word' }}
          >
            {getClientDisplayName(clientUser)}
          </Link>
        ) : (
          <Box component="strong" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {getClientDisplayName(clientUser)}
          </Box>
        )}
      </Typography>
    ) : null
  );

  const renderCheckCreatedLine = () => (
    checkDetail?.check?.createdAt ? (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {formatDateTime(checkDetail.check.createdAt)}
      </Typography>
    ) : null
  );

  const renderPaymentHeaderActions = (stackSx) => (
    canTakePayments && checkDetail?.check?.status !== CHECK_STATUSES.CLOSED ? (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={0.75}
        useFlexGap
        sx={{ width: isMobile ? '100%' : undefined, ...stackSx }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => openPaymentDialog()}
          fullWidth={isMobile}
          data-testid="check-add-payment"
        >
          {isMobile ? t('clients.pay') : t('clients.addPayment')}
        </Button>
        {Number(checkDetail.balance?.amountDue ?? 0) > 0 && (
          <Button
            size="small"
            variant="contained"
            onClick={handlePayAndClose}
            fullWidth={isMobile}
            data-testid="check-pay-and-close"
          >
            {t('clients.payAndClose')}
          </Button>
        )}
      </Stack>
    ) : null
  );

  const renderDeleteEmptyCheckButton = (buttonSx) => (
    showDeleteEmptyCheck ? (
      <Button
        size="small"
        color="error"
        variant="outlined"
        startIcon={buttonStartIcon(isPending('delete-empty-check'), DeleteIcon)}
        onClick={handleDeleteEmptyCheck}
        disabled={isPending('delete-empty-check')}
        fullWidth={isMobile}
        sx={buttonSx}
      >
        {t('checks.deleteEmpty')}
      </Button>
    ) : null
  );

  const headerSlot = checkDetail && !detailLoading ? (
    embedded ? (
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={1}
          flexWrap="wrap"
        >
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <CashierIcon color="primary" fontSize="small" sx={{ flexShrink: 0, mt: 0.25 }} />
              <Box minWidth={0} flex={1}>
                <Typography variant="h6" component="h1" sx={{ wordBreak: 'break-word' }}>
                  {checkTitle}
                </Typography>
                <Chip
                  label={getCheckStatusLabel(checkDetail.check.status, t)}
                  color={getCheckStatusColor(checkDetail.check.status)}
                  size="small"
                  sx={{ mt: 0.75 }}
                />
              </Box>
            </Box>
            {renderCheckClientLine()}
            {renderCheckCreatedLine()}
          </Box>
          {navIds.length > 1 && navIndex >= 0 && (
            <ListNavigation ids={navIds} index={navIndex} onPrev={goToPrev} onNext={goToNext} />
          )}
        </Box>
        {renderDeleteEmptyCheckButton({ mt: 1.5 })}
      </Paper>
    ) : isMobile ? (
      <>
        <Box display="flex" alignItems="center" gap={0.5} mb={1.5} flexWrap="wrap">
          <IconButton onClick={goBack} size="small" aria-label={t(backTarget.labelKey)} sx={{ flexShrink: 0 }}>
            <BackIcon />
          </IconButton>
          {navIds.length > 1 && navIndex >= 0 && (
            <ListNavigation ids={navIds} index={navIndex} onPrev={goToPrev} onNext={goToNext} />
          )}
        </Box>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <CashierIcon color="primary" fontSize="small" sx={{ flexShrink: 0, mt: 0.25 }} />
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" component="h1" sx={{ wordBreak: 'break-word' }}>
                {checkTitle}
              </Typography>
              <Chip
                label={getCheckStatusLabel(checkDetail.check.status, t)}
                color={getCheckStatusColor(checkDetail.check.status)}
                size="small"
                sx={{ mt: 0.75 }}
              />
              {renderCheckClientLine()}
              {renderCheckCreatedLine()}
            </Box>
          </Box>
          {renderPaymentHeaderActions({ mt: 1.5 })}
          {renderDeleteEmptyCheckButton({ mt: 1 })}
        </Paper>
      </>
    ) : (
    <Box display="flex" alignItems="flex-start" gap={1} mb={2} flexWrap="wrap">
      <IconButton onClick={goBack} size="small" sx={{ mt: 0.5, flexShrink: 0 }}>
        <BackIcon />
      </IconButton>
      <Box sx={{ mt: 0.5 }}>
        <ListNavigation ids={navIds} index={navIndex} onPrev={goToPrev} onNext={goToNext} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <CashierIcon color="primary" fontSize="small" />
          <Typography variant="h6" component="h1">
            {checkTitle}
          </Typography>
          <Chip
            label={getCheckStatusLabel(checkDetail.check.status, t)}
            color={getCheckStatusColor(checkDetail.check.status)}
            size="small"
          />
        </Box>
        {renderCheckClientLine()}
        {renderCheckCreatedLine()}
      </Box>
      {renderDeleteEmptyCheckButton({ flexShrink: 0 })}
    </Box>
    )
  ) : null;

  return (
    <CheckDetailPageShell embedded={embedded}>
        {detailLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : checkDetail ? (
          <>
            <CheckDetailView
              check={checkDetail.check}
              balance={checkDetail.balance}
              items={checkDetail.items}
              transactions={checkDetail.transactions}
              isMobile={isMobile}
              isClient={isClient}
              isManager={isManager}
              canRemoveCheckItem={canRemoveCheckItem}
              canEditExchangeRates={(
                isManager
                || (canManageChecks && checkDetail.balance?.maxExchangeRateChangeAmount != null)
              ) && checkDetail.check.status !== CHECK_STATUSES.CLOSED}
              onCheckUpdated={setCheckDetail}
              isInternal={isInternal}
              canEditOrderItemPrice={canEditOrderItemPrice}
              canEditOrderItemWeight={canEditOrderItemWeight}
              canManageOrders={canTakePayments}
              canChangeStatus={canChangeStatus}
              onMarkDelivered={handleItemToDelivered}
              showOrderColumn
              compactBalance
              headerSlot={headerSlot}
              onAddPayment={() => openPaymentDialog()}
              onPayAndClose={handlePayAndClose}
              onCancelPayment={handleCancelPayment}
              onRemoveItem={handleRemoveItem}
              canCancelCheckItems={canCancelCheckItems}
              onBulkRemoveItems={handleBulkRemoveItems}
              onBulkCancelItems={openBulkCancelCheckItems}
              onOpenItemAuditLog={openItemAuditLog}
              onSaveItemEdit={handleSaveItemEdit}
              itemEdits={itemEdits}
              setItemEdit={setItemEdit}
              savingItemId={savingItemId}
              isActionPending={isPending}
              getItemStatusColor={getItemStatusColorClientDetail}
              getItemStatusChipLabel={(item) => getItemStatusLabel(item.status, t)}
              orderLinkClientId={embedded ? clientIdProp : undefined}
              embeddedInClient={embedded}
              canApplyCheckDiscount={canApplyCheckDiscount}
              onCheckDiscountUpdated={handleCheckDiscountUpdated}
              checksBasePath={checksBasePath}
              itemsSectionTitle={t('cashiering.items')}
              noItemsMessage={t('cashiering.noItemsFound')}
              transactionsSectionTitle={t('cashiering.paymentsTransactions')}
              emptyTransactionsMessage={t('cashiering.noTransactions')}
              desktopTransactionsEmptyMode="standalone"
            />
            <AddPaymentDialog
              open={paymentDialogOpen}
              onClose={closePaymentDialog}
              onSubmit={async ({ amount, note, currency, paymentMethod }) => {
                await axios.post(`${checksBasePath}/${checkDetail.check._id}/deposit`, {
                  amount,
                  note: note || undefined,
                  currency: currency || paymentDialogPreset?.currency,
                  paymentMethod: paymentMethod || undefined,
                });
                await fetchCheckDetail();
              }}
              title={t('cashiering.addPaymentTitle')}
              initialAmount={paymentDialogPreset?.amount}
              initialNote={paymentDialogPreset?.note}
              initialCurrency={paymentDialogPreset?.currency}
              defaultCurrency={
                checkDetail.balance?.defaultItemCurrency || checkDetail.balance?.checkCurrency
              }
              submitLabel={paymentDialogPreset?.submitLabel}
              requirePaymentMethod={Boolean(paymentDialogPreset?.requirePaymentMethod)}
              maxAmount={checkDetail.balance?.amountDue}
              checkCurrency={checkDetail.balance?.checkCurrency}
              exchangeRates={checkDetail.check?.exchangeRates || checkDetail.balance?.exchangeRates}
              isMobile={isMobile}
            />
            <BulkCancelOrderItemsDialog
              open={checkCancelDialogOpen}
              onClose={closeBulkCancelCheckItems}
              items={checkCancelItems}
              onItemsChange={setCheckCancelItems}
              onConfirm={confirmBulkCancelCheckItems}
              loading={checkCancelLoading || isPending('bulk-cancel-check-items')}
              isMobile={isMobile}
              t={t}
            />
            <ItemAuditLogDialog
              open={itemAuditOpen}
              onClose={closeItemAuditLog}
              loading={itemAuditLoading}
              logs={itemAuditLogs}
              itemRef={itemAuditRef}
              isMobile={isMobile}
              getAuditStatusLabel={getAuditStatusLabelCashiering}
              getItemStatusColor={getItemStatusColorClientDetail}
              title={t('cashiering.itemAuditLog')}
              closeLabel={t('cashiering.close')}
              emptyMessage={t('cashiering.noAuditLogs')}
              loadingMode="text"
              loadingText={t('cashiering.loadingAuditLogs')}
              mobileCardLayout={false}
              t={t}
              tableHeaderMode="cashiering"
            />
          </>
        ) : (
          <Box py={4}>
            <ExchangeRateMissingAlert errorInfo={detailError} noticeId={`cashiering-check-fx-${checkId}`} />
            {!detailError && (
              <Typography color="text.secondary" gutterBottom align="center">
                {t('cashiering.noChecksFound')}
              </Typography>
            )}
            <Box textAlign="center" mt={2}>
              <Button variant="outlined" onClick={goBack}>
                {t(backTarget.labelKey)}
              </Button>
            </Box>
          </Box>
        )}
        <CheckoutLeftoversDialog
          open={leftoversOpen}
          rows={leftoversRows}
          onClose={closeLeftovers}
        />
    </CheckDetailPageShell>
  );
}

export default CashieringCheckDetail;
